import React, { useEffect, useMemo, useState } from 'react';
import { HDate } from '@hebcal/core';
import { BOAPI } from "../service/UsersService";
import HighlightableTextarea from './HighlightableTextarea';

// ── המרה אמיתית לתאריך עברי דרך @hebcal/core (כבר תלות קיימת בפרויקט) ──
const getHebrewDate = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  return new HDate(date).renderGematriya().replace(/[֑-ׇ]/g, '');
};

const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isSameDay = (a, b) => toDateKey(a) === toDateKey(b);

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const WEEKDAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

// ── בונה את משבצות הלוח לחודש הנתון — כולל ימים "עודפים" מהחודש הקודם/הבא
// כדי למלא שבועות מלאים (6 שורות קבועות, כדי שהלוח לא "יקפוץ" בגובה בין חודשים). ──
const buildMonthGrid = (monthAnchor) => {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0=ראשון
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
};

const HakdashotTab = () => {
  const [hakdashot, setHakdashot] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [monthAnchor, setMonthAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState(null); // null = הפאנל סגור

  useEffect(() => { loadHakdashot(); }, []);

  const loadHakdashot = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchHakdashotData();
      if (Array.isArray(result)) {
        const [statusObj, data] = result;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) {
          setHakdashot(data);
        } else {
          setError(statusObj?.msg || 'שגיאה בטעינת הנתונים');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── מפה מ-"YYYY-MM-DD" לרשומת ההקדשה הפעילה המכסה את היום הזה (אם יש) —
  // מחושבת פעם אחת לכל שינוי ברשימה, לא בכל רינדור של כל תא בלוח. ──
  const activeByDate = useMemo(() => {
    const map = new Map();
    for (const item of hakdashot) {
      if (!item.isActive) continue;
      const start = new Date(item.startDate);
      const end = new Date(item.endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        map.set(toDateKey(d), item);
      }
    }
    return map;
  }, [hakdashot]);

  const monthGrid = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);
  const today = new Date();

  const goPrevMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => { setMonthAnchor(new Date(today.getFullYear(), today.getMonth(), 1)); };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    const key = toDateKey(date);
    const existing = activeByDate.get(key);
    if (existing) {
      setFormData({
        mode: 'edit',
        id: existing.id,
        value: existing.value,
        startDate: toDateKey(new Date(existing.startDate)),
        endDate: toDateKey(new Date(existing.endDate)),
        isActive: existing.isActive,
      });
    } else {
      setFormData({
        mode: 'add',
        value: '',
        startDate: key,
        endDate: key,
        isActive: true,
      });
    }
  };

  const closePanel = () => { setFormData(null); setSelectedDate(null); };

  const handleSave = async () => {
    if (!formData.value || !formData.startDate || !formData.endDate) {
      setError('יש למלא את כל השדות');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const params = {
        value: formData.value,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: formData.isActive,
      };
      const resp = formData.mode === 'edit'
        ? await BOAPI.updateHakdashotValue({ ...params, id: formData.id })
        : await BOAPI.addHakdashotValue(params);

      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadHakdashot();
          closePanel();
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בשמירת ההקדשה');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשמירת ההקדשה');
      console.error("Error:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.center}>טוען הקדשות...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        <h2 style={styles.title}>הקדשות</h2>
        <div style={styles.monthNav}>
          <button onClick={goPrevMonth} style={styles.navBtn}>חודש קודם</button>
          <span style={styles.monthLabel}>{MONTH_NAMES[monthAnchor.getMonth()]} {monthAnchor.getFullYear()}</span>
          <button onClick={goNextMonth} style={styles.navBtn}>חודש הבא</button>
          <button onClick={goToday} style={styles.todayBtn}>היום</button>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={styles.closeErrorButton}>✕</button>
        </div>
      )}

      <div style={styles.calendarWrap}>
        <div style={styles.weekdayRow}>
          {WEEKDAY_LETTERS.map((w) => (
            <div key={w} style={styles.weekdayCell}>{w}</div>
          ))}
        </div>
        <div style={styles.grid}>
          {monthGrid.map((date) => {
            const inMonth = date.getMonth() === monthAnchor.getMonth();
            const dedication = activeByDate.get(toDateKey(date));
            const isToday = isSameDay(date, today);
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            return (
              <div
                key={toDateKey(date)}
                onClick={() => handleDayClick(date)}
                style={{
                  ...styles.dayCell,
                  ...(inMonth ? {} : styles.dayCellOutside),
                  ...(dedication ? styles.dayCellHasDedication : {}),
                  ...(isToday ? styles.dayCellToday : {}),
                  ...(isSelected ? styles.dayCellSelected : {}),
                }}
              >
                <div style={styles.dayCellTop}>
                  <span style={styles.dayNumber}>{date.getDate()}</span>
                  {dedication && <span style={styles.dedicationDot} />}
                </div>
                <div style={styles.hebDateSmall}>{getHebrewDate(date)}</div>
                {dedication && (
                  <div style={styles.dedicationPreview}>{dedication.value}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* פאנל יום נבחר — עריכה/הוספה */}
      {formData && (
        <div style={styles.panelOverlay} onClick={closePanel}>
          <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div style={styles.panelHeader}>
              <h3 style={styles.panelTitle}>
                {formData.mode === 'edit' ? 'עריכת הקדשה' : 'הקדשה חדשה'} — {getHebrewDate(selectedDate)}
              </h3>
              <button onClick={closePanel} style={styles.closeErrorButton}>✕</button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>תיאור ההקדשה *</label>
              <HighlightableTextarea
                value={formData.value}
                onChange={(v) => setFormData(f => ({ ...f, value: v }))}
                rows={3}
                placeholder="הכנס את תיאור ההקדשה"
              />
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>תאריך התחלה *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(f => ({ ...f, startDate: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>תאריך סיום *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(f => ({ ...f, endDate: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(f => ({ ...f, isActive: e.target.checked }))}
                  style={styles.checkbox}
                />
                פעיל
              </label>
            </div>

            <div style={styles.formButtons}>
              <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
                {saving ? '...שומר' : '💾 שמור'}
              </button>
              <button onClick={closePanel} style={styles.cancelButton}>✕ סגור</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },

  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },

  monthNav: { display: 'flex', alignItems: 'center', gap: '10px' },
  navBtn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 'bold', color: '#334155' },
  monthLabel: { fontSize: 16, fontWeight: 'bold', color: '#1a1f36', minWidth: 140, textAlign: 'center' },
  todayBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #C9A84C', background: 'rgba(201,168,76,0.1)', color: '#8a6d1f', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' },

  calendarWrap: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '12px', overflow: 'hidden' },
  weekdayRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', direction: 'rtl' },
  weekdayCell: { textAlign: 'center', padding: '8px 0', fontSize: 12, fontWeight: 'bold', color: '#94A3B8' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '92px', gap: '4px', direction: 'rtl' },

  dayCell: {
    border: '1px solid #eee', borderRadius: '8px', padding: '6px', cursor: 'pointer',
    overflow: 'hidden', backgroundColor: '#fff', transition: 'background-color 0.15s',
  },
  dayCellOutside: { backgroundColor: '#fafafa', color: '#ccc', opacity: 0.6 },
  dayCellHasDedication: { backgroundColor: 'rgba(201,168,76,0.08)', borderColor: 'rgba(201,168,76,0.35)' },
  dayCellToday: { borderColor: '#1f6feb', borderWidth: '2px' },
  dayCellSelected: { boxShadow: '0 0 0 2px #C9A84C inset' },

  dayCellTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dayNumber: { fontSize: 13, fontWeight: 'bold', color: '#333' },
  dedicationDot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#C9A84C' },
  hebDateSmall: { fontSize: 10, color: '#94A3B8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  dedicationPreview: {
    fontSize: 11, color: '#8a6d1f', marginTop: 4, lineHeight: 1.3,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },

  panelOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,23,42,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  panel: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '90%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' },
  panelTitle: { margin: 0, fontSize: '16px', color: '#1a1f36' },

  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'ltr', boxSizing: 'border-box' },
  checkbox: { marginLeft: '5px', cursor: 'pointer' },
  checkboxLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },

  saveButton: { padding: '10px 18px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  cancelButton: { padding: '10px 18px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
};

export default HakdashotTab;
